import { MonthRow, ScheduleResult } from '../domain/types.js';
import { formatMonthLabel, formatRubKopecksPlain } from './format.js';

export type YearTotals = {
  interest: number;
  principal: number;
  overpayment: number;
  total: number;
  balanceEnd: number;
};

export type YearGroup = {
  year: string;
  months: MonthRow[];
  totals: YearTotals;
};

const EXPAND_ALL_LABEL = 'Развернуть все годы';
const COLLAPSE_ALL_LABEL = 'Свернуть все годы';
const EMPTY_TEXT = 'Заполните параметры кредита — здесь появится график платежей.';

let expandedYears: Set<string> | null = null;
const boundElements = new WeakSet<Element>();

/** Groups schedule rows by calendar year (taken from `dateLabel`) with per-year totals in cents. */
export function groupByYear(months: MonthRow[]): YearGroup[] {
  const groups: YearGroup[] = [];
  let current: YearGroup | null = null;
  for (const month of months) {
    const year = month.dateLabel.slice(0, 4);
    if (!current || current.year !== year) {
      current = {
        year,
        months: [],
        totals: { interest: 0, principal: 0, overpayment: 0, total: 0, balanceEnd: month.balanceAfter },
      };
      groups.push(current);
    }
    current.months.push(month);
    current.totals.interest += month.interestPortion;
    current.totals.principal += month.principalPortion;
    current.totals.overpayment += month.overpayment;
    current.totals.total += month.scheduledPayment + month.overpayment;
    current.totals.balanceEnd = month.balanceAfter;
  }
  return groups;
}

function hasEventMarker(month: MonthRow): boolean {
  return Boolean(month.events.overpayment || month.events.refinance || month.events.discountedRate || month.events.paymentRecalculated);
}

function cell(text: string, className?: string): HTMLTableCellElement {
  const td = document.createElement('td');
  td.textContent = text;
  if (className) td.className = className;
  return td;
}

function yearRow(group: YearGroup, expanded: boolean): HTMLTableRowElement {
  const tr = document.createElement('tr');
  tr.className = 'year-row';
  tr.dataset.year = group.year;

  // A real button carries the keyboard interaction and the aria-expanded state; the row click
  // handler serves pointer users.
  const first = document.createElement('td');
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'year-toggle';
  toggle.setAttribute('aria-expanded', String(expanded));
  toggle.setAttribute('aria-label', `${expanded ? 'Свернуть' : 'Развернуть'} ${group.year}`);
  const chevron = document.createElement('span');
  chevron.className = 'chevron';
  chevron.setAttribute('aria-hidden', 'true');
  toggle.append(chevron, group.year);
  first.append(toggle);
  tr.append(
    first,
    money(group.totals.interest),
    money(group.totals.principal),
    group.totals.overpayment > 0 ? money(group.totals.overpayment) : cell('—'),
    money(group.totals.total),
    money(group.totals.balanceEnd),
  );
  return tr;
}

/** Number with a `.unit` ruble suffix that the compact (narrow-container) layout hides. */
function money(cents: number): HTMLTableCellElement {
  const td = cell(formatRubKopecksPlain(cents));
  const unit = document.createElement('span');
  unit.className = 'unit';
  unit.textContent = '\u00a0₽';
  td.append(unit);
  return td;
}

function monthRow(month: MonthRow, year: string, visible: boolean): HTMLTableRowElement {
  const tr = document.createElement('tr');
  tr.className = month.overpayment > 0 ? 'month-row has-overpayment' : 'month-row';
  tr.dataset.year = year;
  tr.hidden = !visible;
  const total = document.createElement('td');
  const strong = document.createElement('strong');
  strong.textContent = formatRubKopecksPlain(month.scheduledPayment + month.overpayment);
  const totalUnit = document.createElement('span');
  totalUnit.className = 'unit';
  totalUnit.textContent = '\u00a0₽';
  total.append(strong, totalUnit);
  tr.append(
    cell(paymentDateLabel(month), hasEventMarker(month) ? 'event-marker' : undefined),
    money(month.interestPortion),
    money(month.principalPortion),
    month.overpayment > 0 ? money(month.overpayment) : cell('—'),
    total,
    money(month.balanceAfter),
  );
  return tr;
}

function setYearExpanded(tbody: HTMLElement, year: string, expanded: boolean): void {
  tbody.querySelectorAll<HTMLButtonElement>(`tr.year-row[data-year="${year}"] button.year-toggle`).forEach(toggle => {
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.setAttribute('aria-label', `${expanded ? 'Свернуть' : 'Развернуть'} ${year}`);
  });
  tbody.querySelectorAll<HTMLTableRowElement>(`tr.month-row[data-year="${year}"]`).forEach(row => {
    row.hidden = !expanded;
  });
  if (!expandedYears) expandedYears = new Set();
  if (expanded) expandedYears.add(year);
  else expandedYears.delete(year);
  updateToggleAllLabel(tbody);
}

function allYears(tbody: HTMLElement): string[] {
  return Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr.year-row')).map(row => row.dataset.year ?? '');
}

function updateToggleAllLabel(tbody: HTMLElement): void {
  const button = document.getElementById('toggle-all-years');
  if (!button) return;
  const years = allYears(tbody);
  const allExpanded = years.length > 0 && years.every(year => expandedYears?.has(year));
  // The label itself says what the button will do, so no aria-pressed (it is not a toggle state).
  button.textContent = allExpanded ? COLLAPSE_ALL_LABEL : EXPAND_ALL_LABEL;
}

function bindOnce(tbody: HTMLElement): void {
  if (!boundElements.has(tbody)) {
    boundElements.add(tbody);
    const toggleFromEvent = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const row = target?.closest<HTMLTableRowElement>('tr.year-row');
      if (!row || !tbody.contains(row)) return;
      const year = row.dataset.year;
      if (!year) return;
      setYearExpanded(tbody, year, !expandedYears?.has(year));
    };
    // Button activation (Enter / Space) bubbles here as a click, so one handler covers both.
    tbody.addEventListener('click', toggleFromEvent);
  }

  const button = document.getElementById('toggle-all-years');
  if (button && !boundElements.has(button)) {
    boundElements.add(button);
    button.addEventListener('click', () => {
      const years = allYears(tbody);
      const allExpanded = years.every(year => expandedYears?.has(year));
      years.forEach(year => setYearExpanded(tbody, year, !allExpanded));
    });
  }
}

export function renderPaymentsTable(schedule: ScheduleResult): void {
  const tbody = document.getElementById('payments-tbody');
  if (!tbody) return;
  bindOnce(tbody);

  if (!schedule.months.length) {
    const tr = document.createElement('tr');
    const td = cell(EMPTY_TEXT, 'no-payments');
    td.colSpan = 6;
    tr.append(td);
    tbody.replaceChildren(tr);
    updateToggleAllLabel(tbody);
    return;
  }

  const groups = groupByYear(schedule.months);
  const years = new Set(groups.map(group => group.year));
  const remembered = expandedYears ? Array.from(expandedYears).filter(year => years.has(year)) : [];
  expandedYears = new Set(remembered.length ? remembered : [groups[0].year]);

  const fragment = document.createDocumentFragment();
  groups.forEach(group => {
    const expanded = expandedYears?.has(group.year) ?? false;
    fragment.append(yearRow(group, expanded));
    group.months.forEach(month => fragment.append(monthRow(month, group.year, expanded)));
  });
  tbody.replaceChildren(fragment);
  updateToggleAllLabel(tbody);

  const exportBtn = document.getElementById('export-payments');
  if (exportBtn) {
    exportBtn.onclick = () => exportPaymentsToCSV(schedule);
  }
}

export function paymentDateLabel(month: MonthRow): string {
  return month.paymentDate ? month.paymentDate.split('-').reverse().join('.') : formatMonthLabel(month.dateLabel);
}

export function exportPaymentsToCSV(schedule: ScheduleResult): void {
  if (!schedule.months.length) return;
  const headers = [schedule.months[0].paymentDate ? 'Дата платежа' : 'Месяц', 'Проценты ₽', 'Погашение долга ₽', 'Досрочный платёж ₽', 'Всего за месяц ₽', 'Остаток долга ₽'];
  const rows = schedule.months.map(month => {
    const totalPayment = month.scheduledPayment + month.overpayment;
    return [
      paymentDateLabel(month),
      (month.interestPortion / 100).toFixed(2),
      (month.principalPortion / 100).toFixed(2),
      month.overpayment > 0 ? (month.overpayment / 100).toFixed(2) : '',
      (totalPayment / 100).toFixed(2),
      (month.balanceAfter / 100).toFixed(2),
    ];
  });
  const csvContent = [headers, ...rows]
    .map(row => row.map(cellValue => `"${cellValue}"`).join(','))
    .join('\n');
  const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `mortgage-payments-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

